/**
 * Toy plastic — the world's shared material policy (DESIGN_SYSTEM v1.1).
 *
 * REPLACES the v1 cel/toon ramp (src/render/toon.ts, deleted). The stepped
 * 3-band look was tried and rejected: the world's props are modelled at
 * REALISTIC scale, and a hard quantized terminator across a 4.5 m car reads as
 * a shading bug rather than as a stylistic choice. The approved target is the
 * render style of `assets/concepts/cyclist_hero.png` — photoreal 3D cartoon /
 * vinyl-toy plastic: smooth soft gradients, glossy-but-not-mirror candy
 * surfaces, studio-soft wraparound light.
 *
 * The look is produced by three cheap things only — materials, a soft
 * environment, and ACES tone mapping. There is deliberately NO SSAO, no SSR,
 * no bloom and no path-traced GI: the budget is a mobile-class GPU.
 *
 *  - materials: `MeshPhysicalMaterial` with clearcoat (this module),
 *  - environment: an analytic per-biome sky/ground gradient on
 *    `scene.environmentNode` (render/envGradient.ts, installed by
 *    render/world.ts). This replaced a RoomEnvironment PMREM on 2026-08-01 and
 *    is the only thing in the rig that resembles GI: it is what makes the
 *    clearcoat sheen carry the biome's own colours.
 *  - tone mapping: ACESFilmic at exposure ~1.1 (src/main.ts).
 *
 * Surface classes (`PlasticKind`) and their parameters live in
 * `PLASTIC_PARAMS`. Loaded GLB materials are classified by name + base colour
 * via `classifyPlastic` (pure, unit-tested); authored materials name their
 * class explicitly at the call site.
 *
 * Vehicles are the one special case: their albedo atlases carry an authored
 * body colour, so `vehiclePaintMaterial` repaints them with a saturation-masked
 * hue swap on a `MeshPhysicalNodeMaterial`. See that function for the details.
 */

import {
  Color,
  MeshLambertMaterial,
  MeshPhysicalMaterial,
  MeshPhysicalNodeMaterial,
  SRGBColorSpace,
  type Material,
  type Mesh,
  type Object3D,
  type Texture,
} from "three/webgpu";
import {
  atan,
  cos,
  fract,
  materialColor,
  mix,
  mod,
  mx_noise_float,
  normalLocal,
  positionLocal,
  positionView,
  positionWorld,
  smoothstep,
  texture,
  uniform,
  vec3,
} from "three/tsl";
import { applyEarthCurve } from "./earthCurve";

/**
 * Surface classes in the toy-plastic policy.
 *
 *  - `candy` — vehicle bodies and other hero saturated surfaces: the glossy
 *    candy-paint sheen (a clearcoat lobe over a fairly smooth base).
 *  - `prop`  — the default for buildings, trees, props and characters: rough
 *    injection-moulded plastic with only a whisper of coat.
 *  - `glass` — dark transparent/tinted vehicle glazing: smooth, still no metal.
 *  - `tire`  — near-black rubber: matte, no coat at all.
 *  - `asphalt` — the road surface: matte grey base with only a WHISPER of
 *    clear coat, so the environment lays one broad soft highlight over it
 *    instead of leaving it flat and lifeless. Never returned by
 *    `classifyPlastic`; authored surfaces ask for it by name.
 */
export type PlasticKind = "candy" | "prop" | "glass" | "tire" | "asphalt";

export interface PlasticParams {
  readonly roughness: number;
  readonly metalness: number;
  readonly clearcoat: number;
  readonly clearcoatRoughness: number;
}

/**
 * The policy table. These are THE numbers from DESIGN_SYSTEM v1.1 — change
 * them here, never at a call site.
 */
export const PLASTIC_PARAMS: Record<PlasticKind, PlasticParams> = {
  candy: { roughness: 0.35, metalness: 0, clearcoat: 0.6, clearcoatRoughness: 0.25 },
  prop: { roughness: 0.55, metalness: 0, clearcoat: 0.15, clearcoatRoughness: 0.35 },
  glass: { roughness: 0.1, metalness: 0, clearcoat: 0.5, clearcoatRoughness: 0.1 },
  tire: { roughness: 0.9, metalness: 0, clearcoat: 0, clearcoatRoughness: 1 },
  // The road. Rougher than any prop so it never reads as a mirror, but the
  // 0.3 coat at 0.3 coat-roughness catches ONE broad, blurred lobe off the
  // gradient environment + sun — the "clear-coat" sheen the rest of the world has,
  // dialled down to the point where the surface still glances as flat matte
  // asphalt. Anything above ~0.4 clearcoat starts streaking the lane markings.
  asphalt: { roughness: 0.5, metalness: 0, clearcoat: 0.3, clearcoatRoughness: 0.3 },
};

/** Relative luminance of an sRGB hex, 0..1. Used by the dark-surface heuristics. */
export function hexLuminance(hex: number): number {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8) & 0xff) / 255;
  const b = (hex & 0xff) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Saturation (HSV S) of an sRGB hex, 0..1. */
export function hexSaturation(hex: number): number {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8) & 0xff) / 255;
  const b = hex & 0xff;
  const bb = b / 255;
  const max = Math.max(r, g, bb);
  const min = Math.min(r, g, bb);
  return max <= 0 ? 0 : (max - min) / max;
}

const GLASS_NAME = /glass|window|windshield|windscreen|glazing|winshield/i;
const TIRE_NAME = /tire|tyre|rubber|wheel/i;
const CANDY_NAME = /body|paint|carpaint|chassis|shell|hull/i;

/** Opaque surfaces below this relative luminance are classified as rubber. */
const TIRE_LUMINANCE_MAX = 0.12;

/**
 * Classify a loaded material into a `PlasticKind`. Pure — the unit tests pin
 * the heuristics, and every GLB conversion goes through here.
 *
 * Order matters: an explicit NAME always wins over the colour heuristics
 * (a GLB that bothers to call a slot "windshield" knows better than we do),
 * then transparency marks glass, then near-black marks rubber.
 *
 * @param name        the source material's name (may be empty)
 * @param colorHex    its base colour as sRGB hex
 * @param transparent whether the source material is transparent
 */
export function classifyPlastic(
  name: string,
  colorHex: number,
  transparent: boolean,
): PlasticKind {
  if (GLASS_NAME.test(name)) return "glass";
  if (TIRE_NAME.test(name)) return "tire";
  if (CANDY_NAME.test(name)) return "candy";
  // Dark + see-through is the shipped vehicles' glazing (design hex #1d2a38).
  if (transparent && hexLuminance(colorHex) < 0.35) return "glass";
  // Near-black opaque surfaces are rubber (tyres, the front-wheel proxy).
  // 0.12 clears the DESIGN wheel hex #17181c (luminance 0.094) with headroom
  // while staying well under the darkest prop colours (conifer #245231 ≈ 0.27).
  if (hexLuminance(colorHex) < TIRE_LUMINANCE_MAX) return "tire";
  // Strongly saturated surfaces read as painted bodywork.
  if (hexSaturation(colorHex) > 0.6) return "candy";
  return "prop";
}

/** Stamp a kind's parameters onto a physical material. */
function applyKind(m: MeshPhysicalMaterial, kind: PlasticKind): void {
  const p = PLASTIC_PARAMS[kind];
  m.roughness = p.roughness;
  m.metalness = p.metalness;
  m.clearcoat = p.clearcoat;
  m.clearcoatRoughness = p.clearcoatRoughness;
}

/**
 * Build a toy-plastic material. Convenience for code that AUTHORS its own
 * materials (guardrails, biome props, cockpit placeholders) instead of
 * converting loaded GLB ones. `kind` defaults to the classifier's verdict on
 * the given name/colour, so most call sites can just pass a colour.
 *
 * WORLD BEND: every material built here carries the curved-earth vertex drop
 * (render/earthCurve.ts) by default, because almost everything this helper
 * builds lives in world space. The handful of CAMERA-ATTACHED surfaces (the
 * cockpit bars, the front-wheel proxy) pass `curve: false` — they must not bend
 * with the world, and their drop would be sub-millimetre anyway.
 */
export function plasticMaterial(params: {
  color?: number;
  map?: Texture | null;
  name?: string;
  kind?: PlasticKind;
  curve?: boolean;
}): MeshPhysicalMaterial {
  const color = params.color ?? 0xffffff;
  const m = new MeshPhysicalMaterial({ color });
  if (params.map) m.map = params.map;
  if (params.name) m.name = params.name;
  applyKind(m, params.kind ?? classifyPlastic(params.name ?? "", color, false));
  if (params.curve !== false) applyEarthCurve(m);
  return m;
}

/**
 * A MATTE, unclearcoated lit material (cheap Lambert path, still receives
 * shadows) — for large flat diffuse planes that must NOT pick up any coat.
 *
 * NOTE: the road no longer uses this. Playtesting read the Lambert asphalt as
 * plain/dead next to the toy-plastic props, so the road moved to
 * `plasticMaterial({ kind: "asphalt" })`; this helper stays for ground planes
 * and backdrops that genuinely want zero sheen.
 */
export function matteMaterial(params: {
  color?: number;
  map?: Texture | null;
  name?: string;
  curve?: boolean;
}): MeshLambertMaterial {
  const m = new MeshLambertMaterial({ color: params.color ?? 0xffffff });
  if (params.map) m.map = params.map;
  if (params.name) m.name = params.name;
  if (params.curve !== false) applyEarthCurve(m);
  return m;
}

/**
 * Convert one loaded material to its toy-plastic equivalent, preserving the
 * base colour / albedo map and the transparency + sidedness setup. The source's
 * roughness/metalness values and their MAPS are dropped on purpose — they are
 * exactly what made the GLBs read as gritty photoreal PBR; the policy table is
 * the single source of truth for surface response.
 *
 * Returns the input unchanged when it must not be converted:
 *  - already a MeshPhysicalMaterial (idempotent; re-running is a no-op),
 *  - `colorWrite === false` (the rider-shadow caster proxies: they draw
 *    nothing and only feed the shadow pass, so shading them is pointless and
 *    converting them risks losing the colorWrite/depthWrite setup),
 *  - not a lit standard/phong/lambert/toon material (MeshBasic stays basic —
 *    the lane markings must never pick up a highlight).
 */
export function toPlastic(src: Material): Material {
  const anyMat = src as Material & {
    isMeshPhysicalMaterial?: boolean;
    isMeshStandardMaterial?: boolean;
    isMeshPhongMaterial?: boolean;
    isMeshLambertMaterial?: boolean;
    isMeshToonMaterial?: boolean;
    color?: { getHex(): number };
    map?: Texture | null;
    alphaMap?: Texture | null;
    emissive?: { getHex(): number };
    emissiveMap?: Texture | null;
    normalMap?: Texture | null;
  };
  if (anyMat.isMeshPhysicalMaterial) return src;
  if (src.colorWrite === false) return src;
  const lit =
    anyMat.isMeshStandardMaterial === true ||
    anyMat.isMeshPhongMaterial === true ||
    anyMat.isMeshLambertMaterial === true ||
    anyMat.isMeshToonMaterial === true;
  if (!lit) return src;

  const hex = anyMat.color ? anyMat.color.getHex() : 0xffffff;
  const out = new MeshPhysicalMaterial();
  out.name = src.name;
  out.color.setHex(hex);
  if (anyMat.map) out.map = anyMat.map;
  if (anyMat.alphaMap) out.alphaMap = anyMat.alphaMap;
  if (anyMat.emissive) out.emissive.setHex(anyMat.emissive.getHex());
  if (anyMat.emissiveMap) out.emissiveMap = anyMat.emissiveMap;
  if (anyMat.normalMap) out.normalMap = anyMat.normalMap;
  out.transparent = src.transparent;
  out.opacity = src.opacity;
  out.alphaTest = src.alphaTest;
  out.side = src.side;
  out.depthWrite = src.depthWrite;
  out.depthTest = src.depthTest;
  out.vertexColors = src.vertexColors;
  out.fog = (src as Material & { fog?: boolean }).fog ?? true;
  applyKind(out, classifyPlastic(src.name, hex, src.transparent));
  return out;
}

/**
 * Convert every lit material under `root` to toy plastic, in place. Materials
 * are converted ONCE and shared (a GLB's meshes usually point at the same
 * material instance), so the returned array is the set of freshly created
 * materials — hand it to the owner's disposer.
 */
export function plasticify(root: Object3D, curve = true): Material[] {
  const cache = new Map<Material, Material>();
  const created: Material[] = [];
  const convert = (m: Material): Material => {
    const hit = cache.get(m);
    if (hit) return hit;
    const out = toPlastic(m);
    // The world bend rides on EVERY material under a world-space GLB, including
    // the ones `toPlastic` hands back untouched (unlit slots, the colour-write
    // -off shadow proxies) — a prop whose trim did not bend with its body would
    // shear apart at distance. `curve: false` is for the camera-attached GLBs
    // (cockpit, kick legs), which must stay rigid in view space.
    if (curve) applyEarthCurve(out);
    cache.set(m, out);
    if (out !== m) created.push(out);
    return out;
  };
  root.traverse((o: Object3D) => {
    const mesh = o as Mesh;
    if (!mesh.material) return;
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(convert)
      : convert(mesh.material as Material);
  });
  return created;
}

/**
 * Saturation window over which a texel counts as "body paint" and gets
 * repainted by `vehiclePaintMaterial`. Measured on the shipped vehicle albedo
 * atlases: the painted body sits at saturation ~1.0, while glass, tyres, rims,
 * lights and trim sit below ~0.3.
 */
const PAINT_SAT_LO = 0.4;
const PAINT_SAT_HI = 0.65;

/**
 * Value (max channel) below which a texel is treated as rubber/dark trim and
 * pushed toward the matte end of the roughness range. Tyres on the shipped
 * atlases sit near 0.05; glass around 0.2.
 */
const DARK_V_LO = 0.0;
const DARK_V_HI = 0.28;
/** Extra roughness added to the darkest unpainted texels (tyres → ~0.85). */
const DARK_ROUGH_BOOST = 0.5;

/**
 * Loose stand-in for a TSL shader node. See the note in
 * `vehiclePaintMaterial`: the real generic node types make the type checker
 * take minutes on this one expression.
 */
interface TslNode {
  readonly [key: string]: unknown;
  max(v: unknown): TslNode;
  min(v: unknown): TslNode;
  sub(v: unknown): TslNode;
  div(v: unknown): TslNode;
  mul(v: unknown): TslNode;
  add(v: unknown): TslNode;
  abs(): TslNode;
  length(): TslNode;
  oneMinus(): TslNode;
}

// ---------------------------------------------------------------------------
// City far-rank silhouette facade (owner playtest 2026-08-02:
// "in the city scene the buildings further away to the side are grey boxes")
// ---------------------------------------------------------------------------

/** Vertical pitch of the window grid (m) — one storey. */
const FACADE_FLOOR_PITCH = 3.2;
/** Horizontal pitch of the window grid (m) — one window bay. */
const FACADE_WINDOW_PITCH = 2.6;
/**
 * Peak albedo swing of the facade, as a fraction of the instance tint.
 *
 * The far rank's job is still to be a HAZE SHAPE, so the budget is deliberately
 * tiny: 0.18 of albedo lands as ~10% of the pixel once the diffuse response and
 * ACES have had it — enough to stop a 26 m box reading as one flat card at
 * 40 m, nowhere near enough to compete with the near hero GLBs for attention.
 */
const FACADE_AMP = 0.18;
/** The inter-floor spandrel band lightens by this fraction of FACADE_AMP. */
const FACADE_SPANDREL = 0.35;
/**
 * The far rank is FULLY MATTE — no clear coat, roughness 1 — instead of the
 * `prop` params every other authored surface uses.
 *
 * Measured while chasing the owner's "grey boxes": halving the far rank's
 * ALBEDO outright moved a near mass only 154→146 of 255, because most of what
 * you see on a 26 m untextured box is not diffuse at all — it is the broad
 * clearcoat + gradient-environment lobe (PLASTIC_PARAMS.prop has clearcoat
 * 0.15 at roughness 0.55). That sheen is what made the masses read as slabs of
 * shiny plastic, and it is also what swallowed the window grid: a ±18% albedo
 * swing under a dominant specular term is invisible. Killing the coat costs
 * nothing (these are haze silhouettes, not toys) and hands the whole visible
 * range back to the albedo, which is where the facade lives.
 */
const FACADE_ROUGHNESS = 1;
/** Windows are glossier than the wall — the one roughness term. */
const FACADE_WINDOW_ROUGH = 0.22;
/**
 * View distances (m) over which the facade fades to flat colour. Past FAR the
 * mass is EXACTLY the flat tinted silhouette it was before, which is what keeps
 * the horizon rank a pure haze shape (and stops a 2.6 m window grid aliasing
 * into a crawl once it is sub-pixel).
 */
const FACADE_FADE_NEAR = 65;
const FACADE_FADE_FAR = 140;

/**
 * The city's FAR-RANK silhouette material: one shared material for every flat
 * box mass, tinted per instance through `instanceColor`, plus a cheap
 * PROCEDURAL WINDOW GRID.
 *
 * Why it exists: the far rank is a unit box scaled 10-26 m wide and parked
 * 33-80 m off the road. Behind two rows of real buildings that reads as a haze
 * shape — but the NEAREST of them are only 30-50 m away, close enough that a
 * single flat lit colour reads as exactly what the owner called it: a grey box.
 *
 * The grid is arithmetic on WORLD coordinates, not a texture and not a
 * per-instance material:
 *
 *   - the vertical address is `positionWorld.y`, so every mass in the rank
 *     shares one floor line rhythm (they all stand on the same street);
 *   - the horizontal address picks the world axis the face looks along
 *     (`|n.z|·x + |n.x|·z` on a box whose local normals are axis-aligned), so
 *     both visible faces of a box get vertical bays instead of one face getting
 *     stripes and the other a smear. `faceMask` is `|n.x| + |n.z|`, which is 0
 *     on the roof — a roof with windows in it is the giveaway of a fake facade.
 *
 * Everything is multiplied by a DISTANCE FADE (see FACADE_FADE_*), so the rank
 * still resolves to flat atmosphere at the horizon.
 *
 * The mass meshes are out of the shadow pass at the call site (biomes.ts sets
 * castShadow/receiveShadow false on the field); nothing here changes that.
 */
export function citySilhouetteMaterial(name = "citySilhouette"): MeshPhysicalNodeMaterial {
  const m = new MeshPhysicalNodeMaterial();
  m.name = name;
  m.roughness = FACADE_ROUGHNESS;
  m.metalness = 0;
  m.clearcoat = 0;
  m.clearcoatRoughness = 1;

  // Same guard as `asphaltMaterial`: the TSL builder generics are ruinously
  // expensive to infer through a chain this long, so every node is the loose
  // `TslNode`.
  const n = normalLocal as unknown as TslNode;
  const nx = (n.x as TslNode).abs();
  const nz = (n.z as TslNode).abs();
  const faceMask = nx.add(nz);
  const pw = positionWorld as unknown as TslNode;
  const u = (pw.x as TslNode).mul(nz).add((pw.z as TslNode).mul(nx));
  const y = pw.y as TslNode;

  /** Signed offset to the centre of the current cell, in [-0.5, 0.5]. */
  const cell = (coord: TslNode, pitch: number): TslNode =>
    (fract(coord.div(pitch) as never) as unknown as TslNode).sub(0.5);
  const cu = cell(u, FACADE_WINDOW_PITCH).abs();
  const cv = cell(y, FACADE_FLOOR_PITCH).abs();
  // 1 inside the window pane, 0 on the mullion / spandrel, soft in between so
  // the grid filters itself instead of aliasing as it shrinks.
  const winU = (smoothstep(0.2, 0.34, cu as never) as unknown as TslNode).oneMinus();
  const winV = (smoothstep(0.18, 0.32, cv as never) as unknown as TslNode).oneMinus();
  const window = winU.mul(winV);
  // The band between floors — a hair LIGHTER than the wall, which is what
  // actually draws the floor lines.
  const spandrel = smoothstep(0.3, 0.46, cv as never) as unknown as TslNode;

  const viewDist = (positionView as unknown as TslNode).length();
  const fade = (
    smoothstep(FACADE_FADE_NEAR, FACADE_FADE_FAR, viewDist as never) as unknown as TslNode
  ).oneMinus();
  const gate = faceMask.mul(fade);

  const detail = spandrel
    .mul(FACADE_AMP * FACADE_SPANDREL)
    .sub(window.mul(FACADE_AMP))
    .mul(gate)
    .add(1);
  m.colorNode = (materialColor as unknown as TslNode).mul(detail) as never;
  // Glass is smoother than concrete. Small — the surface is deliberately matte
  // (see FACADE_ROUGHNESS), so this only puts the faintest sheen difference in
  // the panes; the grid is carried by albedo.
  m.roughnessNode = window
    .mul(gate)
    .mul(-FACADE_WINDOW_ROUGH)
    .add(FACADE_ROUGHNESS) as never;

  applyEarthCurve(m);
  return m;
}

/**
 * A toy-plastic material that REPAINTS a vehicle's body colour.
 *
 * The shipped vehicle GLBs are not neutral: each albedo atlas already carries
 * an authored body colour (the sedan is blue, the hatchback red, …). So a
 * plain `material.color` multiply cannot give the fleet a curated palette — it
 * only darkens/tints whatever the texture already is (and turns light grey
 * rims into the tint colour, which is how the first attempt produced pink
 * wheels on a blue car).
 *
 * Instead this builds a MeshPhysicalNodeMaterial whose colour node performs an
 * HSV hue+saturation SWAP on the saturated texels only:
 *
 *   v      = max(r, g, b)                     (the texel's own value/shading)
 *   sat    = (v - min(r,g,b)) / v
 *   mask   = smoothstep(SAT_LO, SAT_HI, sat)  (1 on body paint, 0 elsewhere)
 *   out    = mix(rgb, normalise(paint) * v, mask)
 *
 * Because the palette colour is normalised to its brightest channel and then
 * scaled by the texel's own `v`, the body keeps ALL of its painted shading
 * (panel gaps, ambient occlusion, highlights) and merely changes hue, while
 * dark glass, black tyres and grey rims pass through untouched. This is the
 * SAME expression the cel-shaded build used — only the host material class
 * changed, from MeshToonNodeMaterial to MeshPhysicalNodeMaterial.
 *
 * A whole vehicle is usually ONE material slot over one atlas, so the surface
 * class has to vary per texel too: the body gets the candy params outright,
 * and a second node raises roughness on the darkest unpainted texels so tyres
 * come out matte rubber instead of glossy candy black.
 *
 * Falls back to a plain candy material with `color = paint` when the model has
 * no albedo map.
 */
export function vehiclePaintMaterial(map: Texture | null, paint: number): MeshPhysicalNodeMaterial {
  const m = new MeshPhysicalNodeMaterial();
  const candy = PLASTIC_PARAMS.candy;
  m.roughness = candy.roughness;
  m.metalness = candy.metalness;
  m.clearcoat = candy.clearcoat;
  m.clearcoatRoughness = candy.clearcoatRoughness;
  // Traffic and wrecks are world-space: they sink with the road (earthCurve.ts),
  // which is what stops a car appearing out of nothing at the 240 m spawn plane.
  applyEarthCurve(m);
  if (!map) {
    m.color.setHex(paint, SRGBColorSpace);
    return m;
  }
  m.map = map;
  // Palette colour in the renderer's working (linear) space, normalised so its
  // brightest channel is 1 — it contributes hue + saturation only.
  //
  // PERF (2026-08-01): this is a UNIFORM, not an inlined `vec3(...)` constant.
  // A constant is baked into the generated WGSL, so every palette colour was a
  // DIFFERENT shader and therefore a different pipeline — ~46 compiles spread
  // across the first minutes of a run, each one a visible hitch on the frame
  // that first drew that colour. As a uniform, all colours of a model share one
  // pipeline (three keys its node-builder cache on the graph, not the value),
  // which cuts the vehicle pipeline count to one per albedo atlas.
  const c = new Color().setHex(paint, SRGBColorSpace);
  const peak = Math.max(c.r, c.g, c.b) || 1;
  const paintN = uniform(new Color(c.r / peak, c.g / peak, c.b / peak));

  // NOTE: the TSL builder types are extremely expensive to infer through a
  // chain this long — leaving them inferred took `tsc --noEmit` from ~10 s to
  // over five minutes. Everything below is deliberately typed as the loose
  // `TslNode` so the checker stops unifying node generics; the shapes are
  // still exercised at runtime by the visual pass.
  const src = texture(map) as unknown as TslNode;
  const rgb = src.rgb as TslNode;
  const v = (rgb.r as TslNode).max(rgb.g).max(rgb.b);
  const lo = (rgb.r as TslNode).min(rgb.g).min(rgb.b);
  const sat = v.sub(lo).div(v.max(1e-4));
  const mask = smoothstep(PAINT_SAT_LO, PAINT_SAT_HI, sat as never);
  m.colorNode = mix(rgb as never, (paintN as unknown as TslNode).mul(v) as never, mask);

  // Per-texel surface class: unpainted AND dark ⇒ rubber. `dark` is 1 on the
  // blackest texels and falls to 0 by DARK_V_HI, gated by (1 - paint mask) so
  // a dark blue or maroon BODY keeps its candy gloss.
  const dark = (smoothstep(DARK_V_LO, DARK_V_HI, v as never) as unknown as TslNode).oneMinus();
  const rubber = dark.mul((mask as unknown as TslNode).oneMinus());
  m.roughnessNode = rubber.mul(DARK_ROUGH_BOOST).add(candy.roughness) as never;
  return m;
}

// ---------------------------------------------------------------------------
// Procedural asphalt (TSL) — playtest 2026-08-01: "the road is too simple"
// ---------------------------------------------------------------------------

/**
 * Peak luminance swing of the large-scale two-tone mottle, as a fraction of the
 * tinted base colour. The whole procedural budget for the road is ~6% (see
 * DESIGN_SYSTEM: detail lives in SHAPE and COLOUR BLOCKING, never in surface
 * noise) and this is the biggest of the three terms; at a glance the road must
 * still read as ONE flat grey.
 */
const ASPHALT_MOTTLE_AMP = 0.034;
/** Peak swing of the fine near-camera speckle (fades out entirely by FADE_FAR). */
const ASPHALT_SPECKLE_AMP = 0.028;
/** Darkening under each wheel track. */
const ASPHALT_TRACK_AMP = 0.03;
/**
 * The matching ROUGHNESS swings (absolute, around PLASTIC_PARAMS.asphalt's
 * 0.5). These are what actually make the detail legible — see the note at the
 * bottom of `asphaltMaterial`. Kept small enough that no patch ever reads as a
 * wet or polished PUDDLE.
 */
const ASPHALT_MOTTLE_ROUGH = 0.1;
const ASPHALT_SPECKLE_ROUGH = 0.05;
const ASPHALT_TRACK_ROUGH = 0.16;
/**
 * View distances (m) over which the speckle fades out. Past FAR it is GONE: a
 * centimetre-scale fleck at 40 m is sub-pixel and would alias into a crawl.
 */
const ASPHALT_SPECKLE_FADE_NEAR = 7;
const ASPHALT_SPECKLE_FADE_FAR = 24;
/** Lateral offset of a wheel track from its lane centre (m), and its soft half-width. */
const ASPHALT_TRACK_OFFSET = 0.78;
const ASPHALT_TRACK_HALF_W = 0.36;

/**
 * The road surface material — see `asphaltMaterial`.
 */
export type AsphaltMaterial = MeshPhysicalNodeMaterial & {
  /** Feed the current travel distance (m) — see the ROAD SPACE note below. */
  setRoadDistance(d: number): void;
};

/**
 * The road surface: the toy-plastic `asphalt` params plus a THREE-TERM
 * procedural detail node. No texture images — everything is arithmetic on the
 * road-space coordinates, so it costs one material and zero bytes of VRAM.
 *
 *   1. a low-contrast large-scale MOTTLE (two crossed sine products, ~2-6 m
 *      patches) that breaks up the dead flat plane at mid distance,
 *   2. a fine SPECKLE that only exists within ~24 m of the camera and fades to
 *      nothing beyond it, so it reads as surface grain right under the bike and
 *      can never shimmer at distance,
 *   3. a slightly darker WHEEL-TRACK band either side of every lane centre —
 *      the one piece of "designed" road detail, and the only term that carries
 *      information (it re-states the lane rhythm).
 *
 * All three MULTIPLY the tinted base colour (`materialColor`, i.e. whatever
 * `World.setRoadTint` last wrote into `material.color`), so the biome tint stays
 * the base that the detail modulates and that hook keeps working untouched. The
 * summed contrast budget is ~±6% luminance: at a glance the road still reads as
 * flat toy-plastic grey, which is the whole point (DESIGN_SYSTEM).
 *
 * ROAD SPACE. The pattern must be glued to the asphalt, not to the world: road
 * chunks stream toward +Z and recycle, so a world-space pattern would slide
 * under the road and a purely object-space one would repeat every 5 m
 * sub-segment. The longitudinal coordinate is therefore
 * `roadDistance - positionWorld.z`, the fragment's stable road-distance address
 * (constant as the chunk scrolls, since a chunk's own z is `distance -
 * anchorD`). `setRoadDistance` must be called with the same interpolated
 * distance `updateWorld` uses. The LATERAL coordinate is `positionLocal.x` —
 * exact metres from the road centreline, which is what the wheel tracks need.
 */
export function asphaltMaterial(params: {
  color: number;
  laneWidth: number;
  /** |x| of the outer lane edge — the tracks stop at the painted edge lines. */
  laneHalfWidth: number;
  name?: string;
}): AsphaltMaterial {
  const m = new MeshPhysicalNodeMaterial() as AsphaltMaterial;
  m.name = params.name ?? "asphalt";
  m.color.setHex(params.color, SRGBColorSpace);
  const p = PLASTIC_PARAMS.asphalt;
  m.roughness = p.roughness;
  m.metalness = p.metalness;
  m.clearcoat = p.clearcoat;
  m.clearcoatRoughness = p.clearcoatRoughness;

  // See the note in `vehiclePaintMaterial`: the TSL builder generics are
  // ruinously expensive to infer through a chain this long, so every node below
  // is deliberately the loose `TslNode`.
  const dist = uniform(0);
  // Metres along the road — stable per surface point as the chunk scrolls.
  const s = (dist as unknown as TslNode).sub((positionWorld as unknown as TslNode).z);
  const u = (positionLocal as unknown as TslNode).x as TslNode; // lateral metres

  // 1. Large mottle. GRADIENT NOISE, not a sum of sines: the first pass built
  // both this and the speckle from products of sines, and a product of sines is
  // a LATTICE — on screen it read as woven fabric stretched over the road,
  // which is exactly the mid-frequency surface noise the design system bans.
  // One `mx_noise_float` octave at ~3 m gives soft irregular two-tone patches.
  const mottle = (
    mx_noise_float(vec3(u.mul(0.34) as never, 0, s.mul(0.29) as never)) as unknown as TslNode
  ).mul(ASPHALT_MOTTLE_AMP);

  // 2. Fine speckle, near camera only — the same noise an order of magnitude
  // finer, faded out entirely before it can alias.
  const viewDist = (positionView as unknown as TslNode).length();
  const nearFade = (
    smoothstep(
      ASPHALT_SPECKLE_FADE_NEAR,
      ASPHALT_SPECKLE_FADE_FAR,
      viewDist as never,
    ) as unknown as TslNode
  ).oneMinus();
  const grain = mx_noise_float(
    vec3(u.mul(7.3) as never, 0, s.mul(6.1) as never),
  ) as unknown as TslNode;
  const speckle = grain.mul(nearFade).mul(ASPHALT_SPECKLE_AMP);

  // 3. Wheel tracks: two soft strips either side of EVERY lane centre. `pc` is
  // the signed offset to the nearest lane centre (the lanes are a uniform
  // laneWidth pitch centred on x = 0), so one expression covers all lanes.
  const half = params.laneWidth / 2;
  const pc = (mod(u.add(half) as never, params.laneWidth) as unknown as TslNode).sub(half);
  const dTrack = pc.abs().sub(ASPHALT_TRACK_OFFSET).abs();
  const onTrack = (
    smoothstep(0, ASPHALT_TRACK_HALF_W, dTrack as never) as unknown as TslNode
  ).oneMinus();
  // Stop at the painted edge lines — the shoulder is not driven on.
  const inLanes = (
    smoothstep(
      params.laneHalfWidth - 0.35,
      params.laneHalfWidth,
      u.abs() as never,
    ) as unknown as TslNode
  ).oneMinus();
  const tracks = onTrack.mul(inLanes).mul(-ASPHALT_TRACK_AMP);

  const detail = mottle.add(speckle).add(tracks).add(1);
  m.colorNode = (materialColor as unknown as TslNode).mul(detail) as never;

  // ROUGHNESS, not just albedo. The road carries a broad clearcoat + PMREM
  // highlight (PLASTIC_PARAMS.asphalt), and that sheen washes a ±6% albedo
  // swing almost flat in the near field — the first pass was invisible on
  // screen at the design-system contrast budget and only became legible when
  // pushed to a cartoon ±12%, which broke "reads flat grey at a glance".
  // Modulating ROUGHNESS instead varies how that existing highlight lands, so
  // the same detail reads without spending any more of the albedo budget: the
  // mottle patches take slightly different sheen, the near speckle gets a
  // micro-sparkle, and the wheel tracks come out POLISHED (smoother than the
  // asphalt around them), which is both what real tracks look like and the
  // cheapest way to make them read.
  const rough = mottle
    .div(ASPHALT_MOTTLE_AMP)
    .mul(ASPHALT_MOTTLE_ROUGH)
    .add(grain.mul(nearFade).mul(ASPHALT_SPECKLE_ROUGH))
    .sub(onTrack.mul(inLanes).mul(ASPHALT_TRACK_ROUGH))
    .add(p.roughness);
  m.roughnessNode = rough as never;

  // The world bend. It only adds a WORLD-SPACE Y offset, so the road-space
  // coordinates above (`positionWorld.z`, `positionLocal.x`) are untouched and
  // the detail pattern stays glued to the asphalt exactly as before.
  applyEarthCurve(m);

  m.setRoadDistance = (d: number): void => {
    (dist as unknown as { value: number }).value = d;
  };
  return m;
}

// ---------------------------------------------------------------------------
// Front-tyre tread (TSL) — the front wheel's SPIN CUE
// ---------------------------------------------------------------------------

/**
 * Angular count of the tread sectors — a 90° period. Two constraints pin this,
 * and 4 is the only count that satisfies both:
 *
 *  - COARSE ENOUGH not to alias. The pattern freezes when the per-frame
 *    rotation is a multiple of the period, i.e. at 28.3 and 56.5 m/s. The
 *    first is a speed at which the motion blur below has already reached 0.985
 *    (no pattern left to freeze) and the second is above the game's top speed.
 *    Three sectors freeze at 37.7 m/s (also blurred out) but six freeze at
 *    18.85 and eight at 14.1 — mid-gameplay, with the pattern still crisp.
 *  - FINE ENOUGH to be a pattern. Only the wheel's crown is on screen, roughly
 *    55° of arc, so a 120° period puts less than half a cycle in frame and the
 *    whole tyre just pulses brighter and darker. At 90° there is always a
 *    light/dark boundary travelling down the visible crown, which is what
 *    reads as ROTATION rather than as flicker.
 */
const TREAD_SECTORS = 4;
/**
 * Softness of a sector edge, as a threshold on `cos(sectors * angle)`. The band
 * is `smoothstep(-EDGE, +EDGE, cos)`, which is symmetric about zero, so the
 * pattern's mean over a revolution is EXACTLY 0.5 — that is what lets the blur
 * term below fade it to a perfectly uniform tyre with a single `oneMinus`.
 */
const TREAD_EDGE = 0.35;
/**
 * Peak albedo swing of a sector, as a fraction of the tyre colour.
 *
 * MEASURED, not chosen. The design brief for this pass asked for ±8-12%, which
 * is the right instinct for a big surface and completely invisible here: the
 * front wheel is seen from almost directly above and behind, so all the player
 * ever gets is a ~40-pixel-wide near-edge-on sliver of NEAR-BLACK rubber, and
 * ±12% of that is two or three display levels. Screenshotted at 3.2x, the tyre
 * was a flat black strip. ±50% keeps both extremes unmistakably dark rubber
 * (0x0d0e12 to 0x262933 around the 0x191b21 base — no colour, no shine, nothing
 * like the rejected coral decals) while giving the sectors enough separation to
 * READ as tread blocks travelling down the crown.
 */
const TREAD_AMP = 0.5;
/**
 * Peak ROUGHNESS swing of a sector (absolute, around PLASTIC_PARAMS.tire's
 * 0.9). This is the term that actually makes the sectors READ: a ±12% albedo
 * swing on a near-black rubber tyre is a handful of display levels, whereas
 * varying how the environment's broad lobe lands on it is free contrast. Same
 * trick, same reason, as the road's procedural detail (`asphaltMaterial`).
 */
const TREAD_ROUGH = 0.35;
/** The sidewall shoulder is a hair lighter than the crown — a seated-tyre cue. */
const TREAD_SIDEWALL_AMP = 0.07;

/**
 * Speeds (m/s) over which the tread pattern MOTION-BLURS out to a uniform tyre.
 *
 * A real wheel's markings read at low speed and smear to nothing at high speed,
 * and that is not just cosmetic here: the pattern's apparent advance per frame
 * is `perFrameRotation mod 360/SECTORS`, which for four sectors freezes at
 * 28.3 m/s. Blur is 0.985 there and a flat 1 by 30 — and the next freeze,
 * 56.5 m/s, is past the game's top speed — so the wagon-wheel effect is never
 * on screen: by the time the pattern would alias, there is no pattern left.
 */
export const TREAD_BLUR_LO = 10;
export const TREAD_BLUR_HI = 30;

/**
 * Motion-blur fraction (0 = crisp sectors, 1 = uniform tyre) for a forward
 * speed in m/s. Pure — the wheel's only per-frame material write goes through
 * this and `treadPhase`.
 */
export function treadBlur(forwardSpeed: number): number {
  const t = Math.min(
    1,
    Math.max(0, (forwardSpeed - TREAD_BLUR_LO) / (TREAD_BLUR_HI - TREAD_BLUR_LO)),
  );
  return t * t * (3 - 2 * t); // smoothstep
}

/**
 * The tread pattern's rotation phase (rad) for a travel distance, wrapped to
 * [0, 2π). SIM DISTANCE, not wall clock: a paused / title / game-over state has
 * a frozen distance, so the wheel is frozen too, for free.
 */
export function treadPhase(distance: number, wheelRadius: number): number {
  const TAU = Math.PI * 2;
  const p = (distance / wheelRadius) % TAU;
  return p < 0 ? p + TAU : p;
}

/** The front tyre's material — see `tyreTreadMaterial`. */
export type TyreTreadMaterial = MeshPhysicalNodeMaterial & {
  /**
   * Per-frame spin write. `phase` is `treadPhase(distance, radius)` and `blur`
   * is `treadBlur(forwardSpeed)`. Two uniform writes — no pipeline is touched.
   */
  setSpin(phase: number, blur: number): void;
};

/**
 * The front wheel's tyre: matte rubber (`tire` params) carrying a PROCEDURAL,
 * PHASE-ROTATED tread pattern.
 *
 * WHY A SHADER PHASE AND NOT GEOMETRY. The first spin cue was a pair of coral
 * arcs modelled onto the tread. It was rejected on sight, and the reason is
 * structural: at 17-44 m/s on a 0.30 m wheel any discrete marker teleports
 * 54-140° per frame, so it never reads as rotation — it reads as a colour
 * flashing at random places on the tyre. The pattern here is instead SHADED,
 * from the fragment's own angle about the spin axis plus a `phase` uniform, so
 * the tread geometry is a plain torus that never moves. That buys two things:
 * the pattern can be broad and low-frequency (three soft sectors, no hard
 * edges to alias), and — because a uniform write costs nothing and compiles
 * nothing — it can be crossfaded to a flat tyre with speed. See TREAD_BLUR_*:
 * at top speed the crown reads as clean rubber with a faint moving sheen,
 * which is exactly what a real wheel looks like at 44 m/s.
 *
 * ANGLE. `atan(positionLocal.y, positionLocal.z)` — the fragment's angle about
 * the LOCAL +X axis, which is the wheel's rolling axis after `buildWheel`'s
 * `rotateY(90°)`. Object space, so lean, steer and the camera's own pose drop
 * out for free (and the cockpit opts out of `applyEarthCurve` anyway, which is
 * why `curve` is not applied here).
 */
export function tyreTreadMaterial(params: {
  color: number;
  name?: string;
  /** Tyre tube radius (m) — sets where the sidewall shoulder starts. */
  tube: number;
}): TyreTreadMaterial {
  const m = new MeshPhysicalNodeMaterial() as TyreTreadMaterial;
  m.name = params.name ?? "frontTyre";
  m.color.setHex(params.color, SRGBColorSpace);
  const p = PLASTIC_PARAMS.tire;
  m.roughness = p.roughness;
  m.metalness = p.metalness;
  m.clearcoat = p.clearcoat;
  m.clearcoatRoughness = p.clearcoatRoughness;

  // Same guard as `asphaltMaterial` / `vehiclePaintMaterial`: the TSL builder
  // generics are ruinously expensive to infer through a chain this long, so
  // every node below is deliberately the loose `TslNode`.
  const phase = uniform(0);
  const blur = uniform(0);
  const pl = positionLocal as unknown as TslNode;
  const theta = atan(pl.y as never, pl.z as never) as unknown as TslNode;
  // SIGN (owner playtest 2026-08-02: "the wheel spin looks backwards").
  // The MESH rolls by rotation.x = -phase, and rotating geometry about +X by
  // α moves a surface point at θ = atan2(y, z) to θ - α — so the wheel's real
  // features live at θ + phase. A static pattern shaded at (θ + phase) puts
  // its features at θ = -phase, i.e. COUNTER-rotating against the roll. The
  // pattern must be sampled at (θ - phase) to travel with the geometry.
  const wave = cos(
    theta.sub(phase as unknown as TslNode).mul(TREAD_SECTORS) as never,
  ) as unknown as TslNode;
  // 1 inside a light sector, 0 in the gap, soft across the shoulder. Symmetric
  // about cos = 0, so its mean over a revolution is exactly 0.5.
  const band = smoothstep(-TREAD_EDGE, TREAD_EDGE, wave as never) as unknown as TslNode;
  // Zero-mean, and crossfaded to its own mean (i.e. to nothing) by `blur`.
  const sector = band
    .sub(0.5)
    .mul(2)
    .mul((blur as unknown as TslNode).oneMinus());
  // Sidewall shoulder: |x| is the distance across the tyre's section.
  const shoulder = smoothstep(
    params.tube * 0.35,
    params.tube * 0.9,
    (pl.x as TslNode).abs() as never,
  ) as unknown as TslNode;

  const detail = sector.mul(TREAD_AMP).add(shoulder.mul(TREAD_SIDEWALL_AMP)).add(1);
  m.colorNode = (materialColor as unknown as TslNode).mul(detail) as never;
  // The legibility term (see TREAD_ROUGH): the light sectors are SMOOTHER, so
  // the environment's broad lobe travels round the crown with them.
  m.roughnessNode = sector.mul(-TREAD_ROUGH).add(p.roughness) as never;

  m.setSpin = (ph: number, bl: number): void => {
    (phase as unknown as { value: number }).value = ph;
    (blur as unknown as { value: number }).value = bl;
  };
  return m;
}
